import React, { useEffect, useState, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AutoMatchProvider } from './context/AutoMatchContext';
import FloatingAutoMatchProgress from './components/FloatingAutoMatchProgress';
import Navbar from './components/Navbar';
import SessionReplaceWarning from './components/SessionReplaceWarning';
import HomePage from './pages/HomePage';
import MaintenancePage from './pages/MaintenancePage';
import ErrorBoundary from './components/ErrorBoundary';
import { Toaster } from 'sonner';
import axios from 'axios';
import { API_BASE_URL } from './config/api';
import { cachedGet } from './utils/apiCache';
// Import error logger to initialize global handlers
import './utils/errorLogger';

// Lazy-loaded pages (code-splitting). HomePage stays eager for fast LCP.
const BooksPage = lazy(() => import('./pages/BooksPage'));
const BookDetailPage = lazy(() => import('./pages/BookDetailPage'));
const TagBooksPage = lazy(() => import('./pages/TagBooksPage'));
const SeriesPage = lazy(() => import('./pages/SeriesPage'));
const BrandsPage = lazy(() => import('./pages/BrandsPage'));
const BrandDetailPage = lazy(() => import('./pages/BrandDetailPage'));
const AuthorsPage = lazy(() => import('./pages/AuthorsPage'));
const AuthorDetailPage = lazy(() => import('./pages/AuthorDetailPage'));
const AirtableTestPage = lazy(() => import('./pages/AirtableTestPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PublicPage = lazy(() => import('./pages/PublicPage'));

// Legacy English path → Turkish CMS page route
const LegacyPageRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/sayfa/${slug || ''}`} replace />;
};

// Minimal route fallback (matches site palette without importing extra deps)
const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg, #F9F8F6)' }}>
    <div className="text-center">
      <div className="inline-block w-10 h-10 border-4 rounded-full animate-spin"
           style={{ borderColor: 'var(--theme-border, #e5e7eb)', borderTopColor: 'var(--theme-primary, #7A2E24)' }} />
      <p className="mt-4 text-sm" style={{ color: 'var(--theme-text-secondary, #6b7280)' }}>Yükleniyor…</p>
    </div>
  </div>
);

const BACKEND_URL = API_BASE_URL;

// Maintenance check wrapper component
const MaintenanceWrapper = ({ children }) => {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [siteName, setSiteName] = useState('');
  const [checked, setChecked] = useState(false);
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  
  useEffect(() => {
    let cancelled = false;
    const checkMaintenance = async () => {
      try {
        const [maintenanceRes, settingsRes] = await Promise.all([
          cachedGet(`${BACKEND_URL}/api/settings/maintenance-status`),
          cachedGet(`${BACKEND_URL}/api/settings/`)
        ]);
        if (cancelled) return;
        setMaintenanceMode(maintenanceRes.data.maintenance_mode);
        setMaintenanceMessage(maintenanceRes.data.maintenance_message);
        setLogoUrl(settingsRes.data.logo_url || '');
        setSiteName(settingsRes.data.site_name || '');
      } catch (error) {
        if (!cancelled) setMaintenanceMode(false);
      } finally {
        if (!cancelled) setChecked(true);
      }
    };
    checkMaintenance();
    return () => { cancelled = true; };
  }, []);
  
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLoginPage = location.pathname === '/login' || location.pathname === '/register';
  const isAuthCallback = location.pathname === '/auth/callback';
  
  if (isAdminRoute || isLoginPage || isAuthCallback) {
    return children;
  }
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  // Show maintenance page only after check completes and maintenance is ON
  if (checked && maintenanceMode && !isAdmin) {
    return <MaintenancePage message={maintenanceMessage} logoUrl={logoUrl} siteName={siteName} />;
  }
  
  // Render content immediately - don't block on maintenance check
  return children;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Hide public navbar on admin routes
const ConditionalNavbar = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return <Navbar />;
};

function App() {
  useEffect(() => {
    // Fetch and apply site settings
    const fetchSiteSettings = async () => {
      try {
        const response = await cachedGet(`${BACKEND_URL}/api/settings/`);
        const settings = response.data;

        // Update page title
        if (settings.meta_title) {
          document.title = settings.meta_title;
        } else if (settings.site_name) {
          document.title = settings.site_name;
        }

        // Update meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.name = 'description';
          document.head.appendChild(metaDescription);
        }
        if (settings.meta_description) {
          metaDescription.content = settings.meta_description;
        }

        // Update meta keywords
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (!metaKeywords && settings.meta_keywords) {
          metaKeywords = document.createElement('meta');
          metaKeywords.name = 'keywords';
          metaKeywords.content = settings.meta_keywords;
          document.head.appendChild(metaKeywords);
        } else if (metaKeywords && settings.meta_keywords) {
          metaKeywords.content = settings.meta_keywords;
        }

        // Update favicon
        if (settings.favicon_url) {
          let favicon = document.querySelector('link[rel="icon"]');
          if (!favicon) {
            favicon = document.createElement('link');
            favicon.rel = 'icon';
            document.head.appendChild(favicon);
          }
          favicon.href = `${BACKEND_URL}${settings.favicon_url}`;
        }

        // Update Open Graph meta tags
        if (settings.og_image) {
          let ogImage = document.querySelector('meta[property="og:image"]');
          if (!ogImage) {
            ogImage = document.createElement('meta');
            ogImage.setAttribute('property', 'og:image');
            document.head.appendChild(ogImage);
          }
          ogImage.content = `${BACKEND_URL}${settings.og_image}`;
        }

        if (settings.meta_title) {
          let ogTitle = document.querySelector('meta[property="og:title"]');
          if (!ogTitle) {
            ogTitle = document.createElement('meta');
            ogTitle.setAttribute('property', 'og:title');
            document.head.appendChild(ogTitle);
          }
          ogTitle.content = settings.meta_title;
        }

        if (settings.meta_description) {
          let ogDescription = document.querySelector('meta[property="og:description"]');
          if (!ogDescription) {
            ogDescription = document.createElement('meta');
            ogDescription.setAttribute('property', 'og:description');
            document.head.appendChild(ogDescription);
          }
          ogDescription.content = settings.meta_description;
        }

      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };

    fetchSiteSettings();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PermissionsProvider>
          <ThemeProvider>
          <AutoMatchProvider>
            <ErrorBoundary componentName="App">
              <ScrollToTop />
              <MaintenanceWrapper>
                <ConditionalNavbar />
                <SessionReplaceWarning />
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/books" element={<BooksPage />} />
                  <Route path="/kitaplar" element={<BooksPage />} />
                  <Route path="/books/:id" element={<BookDetailPage />} />
                  <Route path="/tags/:tagSlug" element={<TagBooksPage />} />
                  <Route path="/series" element={<SeriesPage />} />
                  <Route path="/diziler" element={<SeriesPage />} />
                  <Route path="/brands" element={<BrandsPage />} />
                  <Route path="/markalar" element={<BrandsPage />} />
                  <Route path="/brands/:id" element={<BrandDetailPage />} />
                  <Route path="/markalar/:id" element={<BrandDetailPage />} />
                  <Route path="/authors" element={<AuthorsPage />} />
                  <Route path="/yazarlar" element={<AuthorsPage />} />
                  <Route path="/authors/:name" element={<AuthorDetailPage />} />
                  <Route path="/yazarlar/:name" element={<AuthorDetailPage />} />
                  <Route path="/sayfa/:slug" element={<PublicPage />} />
                  <Route path="/page/:slug" element={<LegacyPageRedirect />} />
                  <Route path="/iletisim" element={<Navigate to="/sayfa/iletisim" replace />} />
                  <Route path="/hakkimizda" element={<Navigate to="/sayfa/hakkimizda" replace />} />
                  <Route path="/kvkk" element={<Navigate to="/sayfa/kvkk" replace />} />
                  <Route path="/airtable-test" element={<AirtableTestPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/giris" element={<LoginPage />} />
                  <Route path="/admin/*" element={<AdminDashboard />} />
                  <Route path="/auth/callback" element={<OAuthCallback />} />
                </Routes>
                </Suspense>
                <FloatingAutoMatchProgress />
              </MaintenanceWrapper>
            </ErrorBoundary>
          </AutoMatchProvider>
          </ThemeProvider>
          </PermissionsProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#1f2937',
            color: '#f9fafb',
            border: '1px solid #374151',
          },
          className: 'sonner-toast',
        }}
        richColors
        closeButton
      />
    </div>
  );
}

export default App;